import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Floor, getFloorsByBuildingId, useAppSelector} from '@lib/store';
import {Icon, IconNames} from '@atoms';
import {cn} from '@utils';
import {animated, config, useTransition} from 'react-spring';
import {useHereAndNow} from '../Context/HereAndNowContext';
import {trackEvent} from '@lib/infrastructure';
import {useOutsideActionHandler} from './useOutsideActionHandler';

const MAX_FLOORS_DISPLAYED = 10;
const ITEM_HEIGHT = 48;

export const FloorSelector = () => {
  const {tab, hasAnyStats, buildingId, selectedFloorId, setSelectedFloorId} = useHereAndNow();
  const floors = useAppSelector((state) => getFloorsByBuildingId(state, buildingId));
  const [isOpen, setIsOpen] = useState(false);
  const {setSelectedObject, setSelectedObjectForMap} = useHereAndNow();

  useEffect(() => {
    if (floors.length > 0 && !selectedFloorId) {
      setSelectedFloorId(floors[0].id);
    }
  }, [floors, selectedFloorId, setSelectedFloorId]);

  const closeSelector = useCallback(() => {
    setIsOpen(false);

    trackEvent('HereAndNow_FloorSelector__Close', {
      buildingId,
      tab,
      currentFloorId: selectedFloorId || '',
    });
  }, [buildingId, tab, selectedFloorId]);

  const selectorRef = useOutsideActionHandler<HTMLDivElement>({
    action: closeSelector,
    enabled: isOpen,
    delay: 300,
  });

  const toggleSelector = () => {
    setIsOpen((prev) => {
      const next = !prev;
      if (next) {
        trackEvent('HereAndNow_FloorSelector__Open', {
          buildingId,
          tab,
          currentFloorId: selectedFloorId || '',
          hasUsageStats: hasAnyStats,
        });
      } else {
        trackEvent('HereAndNow_FloorSelector__Close', {
          buildingId,
          tab,
          currentFloorId: selectedFloorId || '',
        });
      }
      return next;
    });
  };

  const handleFloorChange = (floor: Floor) => {
    setSelectedFloorId(floor.id);

    setSelectedObject(null);
    setSelectedObjectForMap(null);

    trackEvent('HereAndNow_FloorSelector__Select', {
      buildingId,
      tab,
      currentFloorId: selectedFloorId || '',
      hasUsageStats: hasAnyStats,
      floorId: floor.id,
      floorNumber: floor.number,
    });

    closeSelector();
  };

  const sortedFloors = useMemo(() => [...floors].sort((a, b) => b.number - a.number), [floors]);

  const numberOfPages = useMemo(() => {
    return Math.ceil(sortedFloors.length / MAX_FLOORS_DISPLAYED);
  }, [sortedFloors]);

  const currentSelectedPageIndex = useMemo(() => {
    // this is inverted because floors are shown from highest to lowest when looking at the dom
    if (!selectedFloorId) return 0;
    const selectedFloorIndex = sortedFloors.findIndex((floor) => floor.id === selectedFloorId);
    const totalPages = Math.ceil(sortedFloors.length / MAX_FLOORS_DISPLAYED);
    const normalPageIndex = Math.floor(selectedFloorIndex / MAX_FLOORS_DISPLAYED);
    return totalPages - 1 - normalPageIndex;
  }, [selectedFloorId, sortedFloors]);

  const [currentPageIndex, setCurrentPageIndex] = useState(currentSelectedPageIndex);
  const showNavigation = numberOfPages > 1;

  const calculated = useMemo(() => {
    const totalFloors = floors.length;
    const remainder = totalFloors % MAX_FLOORS_DISPLAYED;

    if (currentPageIndex === 0) {
      return 0;
    }

    if (remainder > 0 && currentPageIndex === numberOfPages - 1) {
      return currentPageIndex - 1 + remainder / 10;
    }

    return currentPageIndex;
  }, [currentPageIndex, floors.length, numberOfPages]);

  const handlePreviousPage = () => {
    setCurrentPageIndex((prev) => Math.min(numberOfPages - 1, prev + 1));
    trackEvent('HereAndNow_FloorSelector__PreviousPage', {
      buildingId,
      tab,
      currentFloorId: selectedFloorId || '',
      pageCount: numberOfPages,
      floorCount: floors.length,
    });
  };

  const handleNextPage = () => {
    setCurrentPageIndex((prev) => Math.max(0, prev - 1));
    trackEvent('HereAndNow_FloorSelector__NextPage', {
      buildingId,
      tab,
      currentFloorId: selectedFloorId || '',
      pageCount: numberOfPages,
      floorCount: floors.length,
    });
  };

  const canNavigatePrevious = currentPageIndex < numberOfPages - 1;
  const canNavigateNext = currentPageIndex > 0;

  const transition = useTransition(isOpen, {
    delay: 100,
    initial: {opacity: 1, transform: 'translate3d(0, 0px, 0) scale(1)'},
    from: {opacity: 0, transform: 'translate3d(0, 10px, 0) scale(1)'},
    enter: {opacity: 1, transform: 'translate3d(0, 0px, 0) scale(1)'},
    leave: {opacity: 0, transform: 'translate3d(0, 10px, 0) scale(1)'},
    config: config.stiff,
  });

  const selectedFloor = useMemo(() => {
    return floors.find((floor) => floor.id === selectedFloorId);
  }, [floors, selectedFloorId]);

  return (
    <div
      ref={selectorRef}
      className="pointer-events-auto  z-10 flex justify-end items-end gap-2 self-end"
      style={
        {
          '--maxHeight': `${MAX_FLOORS_DISPLAYED * ITEM_HEIGHT}px`,
        } as React.CSSProperties
      }>
      {transition(
        (style, item) =>
          item && (
            <animated.div
              style={style}
              className="overflow-clip rounded-lg shadow-lg">
              {showNavigation && (
                <NavigationButton
                  icon="caretUp"
                  disabled={!canNavigatePrevious}
                  onClick={handlePreviousPage}
                />
              )}

              <FloorList
                floors={sortedFloors}
                selectedFloorId={selectedFloorId}
                onFloorChange={handleFloorChange}
                calculated={calculated}
                showNavigation={showNavigation}
              />

              {showNavigation && (
                <NavigationButton
                  icon="caretDown"
                  onClick={handleNextPage}
                  disabled={!canNavigateNext}
                  className="border-t border-b"
                />
              )}
            </animated.div>
          ),
      )}

      <button
        className={cn(
          'relative flex justify-center items-center gap-2 py-2 px-4 md:h-12 bg-white rounded-lg shadow-lg transition-all hover:bg-gray-50 border-2 border-transparent -m-0.5 md:m-0 md:border-none',
          {'border-mapiq-black-800* border-2': isOpen},
        )}
        onClick={toggleSelector}>
        {selectedFloor && <h4>{selectedFloor?.number}</h4>}
        <Icon
          icon="floor"
          className="size-4 md:size-5"
        />
      </button>
    </div>
  );
};

const NavigationButton = ({
  icon,
  onClick,
  className,
  disabled,
}: {
  icon: IconNames;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
}) => {
  return (
    <button
      className={cn(
        'items-center px-5 py-4 gap-2 flex justify-center w-full bg-white border-b z-10 transition-colors',
        {'text-mapiq-black-200': disabled},
        className,
      )}
      style={{
        height: `calc(var(--maxHeight) / ${MAX_FLOORS_DISPLAYED})`,
      }}
      onClick={onClick}
      disabled={disabled}>
      <Icon
        icon={icon}
        color="currentColor"
        className="size-5"
      />
    </button>
  );
};

const FloorList = ({
  floors,
  selectedFloorId,
  onFloorChange,
  calculated,
  showNavigation,
}: {
  floors: Floor[];
  selectedFloorId: string | null;
  onFloorChange: (floor: Floor) => void;
  calculated: number;
  showNavigation: boolean;
}) => {
  const {hasAnyStats} = useHereAndNow();

  return (
    <div className="flex overflow-hidden size-full transition-all shadow-lg pointer-events-auto max-h-[var(--maxHeight)]">
      <div
        className="flex items-end"
        style={{
          transform: `translateY(${calculated * 100}%)`,
          transition: 'transform 0.3s ease-in-out',
        }}>
        <div
          className={cn('grid grid-cols-1 origin-bottom', {
            'grid-cols-[auto_auto_auto]': hasAnyStats,
          })}>
          {floors.map((floor, index) => (
            <FloorSelectorItem
              className={cn('border-b', {
                '[&:last-child] border-b-0': showNavigation,
              })}
              key={`floor-${floor.id}-${index}`}
              floor={floor}
              selected={floor.id === selectedFloorId}
              onClick={onFloorChange}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const FloorSelectorItem = ({
  floor,
  onClick,
  selected,
  className,
}: {
  floor: Floor;
  onClick: (f: Floor) => void;
  selected: boolean;
  className?: string;
}) => {
  const {usageStatsPerFloor} = useHereAndNow();
  const usageStats = usageStatsPerFloor.get(floor.id);

  return (
    <button
      className={cn(
        'items-center px-5 py-2 rounded-none gap-2 relative grid grid-cols-subgrid bg-white col-span-full hover:bg-mapiq-black-50 border-b',
        'before:absolute before:w-[4px] before:inset-0 before:right-auto before:transition-transform before:-translate-x-full before:origin-left',
        {
          'bg-mapiq-black-50 before:translate-x-0 before:bg-mapiq-black-800*': selected,
        },
        className,
      )}
      style={{
        height: `calc(var(--maxHeight) / ${MAX_FLOORS_DISPLAYED})`,
      }}
      onClick={() => onClick(floor)}>
      <h4 className="translate-y-[10%] text-center">{floor.number}</h4>
      {usageStats && (
        <>
          <div
            className={cn('w-10 rounded-sm', {
              'bg-mapiq-black-100': usageStats.ratio === 0,
            })}>
            <span
              className="block bg-functional-green h-1.5 transition-[width] rounded-sm transform origin-left"
              style={{
                width: `${usageStats.ratio * 100}%`,
              }}
            />
          </div>
          <small className="text-mapiq-black-500 text-right">{`${usageStats.available}/${usageStats.total}`}</small>
        </>
      )}
    </button>
  );
};
